<template>
  <div v-if="state && (state.canVerify || state.canViewBenefits)" ref="root" class="iqv">
    <button
      v-if="state.canVerify"
      type="button"
      class="iqv__btn"
      :disabled="state.busy"
      :aria-expanded="mode ? 'true' : 'false'"
      aria-haspopup="dialog"
      data-test="ppp-iv-quick-btn"
      @click="onButton"
    >
      <i v-if="state.busy" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
      {{ state.busy ? 'Checking…' : state.label }}
    </button>

    <!--
      Reading what the practice already paid for: free, so it is not behind canVerify and not
      disabled while a check is busy. It only EMITS `benefits`; the card opens its own drawer.
    -->
    <button
      v-if="state.canViewBenefits"
      type="button"
      class="iqv__btn iqv__btn--ghost"
      data-test="ppp-iv-quick-benefits"
      @click="onBenefits"
    >
      <i class="fa fa-file-text-o" aria-hidden="true"></i>
      View benefits
    </button>

    <!--
      ⛔ This box is a copy of InsurancePanel's `.ip-confirm`, word for word where it counts: one
      click on Run check is a real billed eligibility check, and the sentence must say so. It only
      ever EMITS `run`; the Insurance card (the one place that holds the double-billing lock and
      always sends an explicit ordinal) does the dispatching. Never import the store here.
    -->
    <div
      v-if="mode === 'confirm'"
      ref="box"
      class="iqv__box"
      :style="boxStyle"
      role="dialog"
      aria-label="Run an eligibility check"
      data-test="ppp-iv-quick-confirm"
    >
      <div class="iqv__title">Run an eligibility check?</div>
      <div class="iqv__facts">
        <div><span>Patient</span><strong>{{ patientLabel }}</strong></div>
        <div><span>Carrier</span><strong>{{ state.carrierName || '—' }}</strong></div>
        <div><span>Member ID</span><strong>{{ state.memberId || '—' }}</strong></div>
      </div>
      <p class="iqv__cost">
        This runs <strong>1 eligibility check</strong> against
        {{ state.carrierName || 'this carrier' }}
        (primary plan) and is billed whether or not the carrier finds the patient.
      </p>
      <div class="iqv__actions">
        <button
          ref="cancel"
          type="button"
          class="iqv__act iqv__act--ghost"
          data-test="ppp-iv-quick-cancel"
          @click="close"
        >Cancel</button>
        <button
          type="button"
          class="iqv__act iqv__act--primary"
          :disabled="state.busy"
          data-test="ppp-iv-quick-run"
          @click="onRun"
        >Run check</button>
      </div>
    </div>

    <!-- What happened to the check run from here — so a failure is not toast-only. -->
    <div
      v-else-if="mode === 'outcome' && state.outcome"
      ref="box"
      class="iqv__box"
      :style="boxStyle"
      role="dialog"
      aria-label="Eligibility check result"
      data-test="ppp-iv-quick-outcome"
    >
      <div class="iqv__title">Eligibility check</div>
      <p class="iqv__line" :class="{ 'iqv__line--muted': state.outcome.tone === 'muted' }">
        <i v-if="state.busy" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
        <i v-else class="fa fa-exclamation-triangle" aria-hidden="true"></i>
        <span>{{ state.outcome.text }}</span>
      </p>
      <div class="iqv__actions">
        <button
          ref="closeBtn"
          type="button"
          class="iqv__act iqv__act--ghost"
          data-test="ppp-iv-quick-close"
          @click="close"
        >Close</button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * The "Verify" / "Re-check" button inside the Patient Profile header's "Eligibility last verified"
 * chip (2026-10-11), with the small confirm that opens under it, and the "View benefits" button
 * beside it.
 *
 * PRESENTATIONAL, on purpose. It is a remote for the Insurance card (InsurancePanel.vue), which is
 * the ONLY thing that dispatches a check: it holds the double-billing lock (`verifying`, and
 * `followUpActive` while a check that got no answer is followed up), always sends an explicit
 * ordinal, and writes the outcome line. This component draws what the card reports (`state` — the
 * card's `primary-state`), asks, and emits `run`. The page forwards that to the card's
 * runPrimaryFromHeader(). It imports no store and no axios, and it must never mention the verify
 * action (pinned by cypress/node-tests/insurance-verification-profile-panel.test.js).
 *
 *   state { canVerify, canViewBenefits, label, busy, verifying, carrierName, memberId,
 *           outcome: { tone, text } | null }
 *     neither canVerify nor canViewBenefits (or no state) ⇒ nothing is drawn.
 *     canVerify false ⇒ no Verify / Re-check button and no box (the spend gate).
 *     busy ⇒ the Verify button is disabled and says "Checking…".
 *     canViewBenefits ⇒ "View benefits", which emits `benefits` (the page forwards it to the card's
 *       openPrimaryBenefits()). Reading a stored result is FREE, so it is NOT behind canVerify, is
 *       NOT disabled while busy, and never emits `run`.
 *   Run check emits `run` ONCE and closes the box. Cancel, Escape and a click outside close it with no
 *   emit. When the card then reports an outcome for a run started HERE, the box reopens with it; an
 *   outcome nobody started here (the card's own button, the drawer) never opens it.
 *
 * ⛔ No prop named `patNum`, `siteID` or `loading` — mixin.js computeds silently replace them.
 */
const BOX_WIDTH = 300;
const EDGE = 16;

export default {
  name: 'InsuranceQuickVerify',

  props: {
    /** The card's primary-state, or null until it reports. */
    state: { type: Object, default: null },
    patientLabel: { type: String, default: 'this patient' },
  },

  data() {
    return {
      // '' closed · 'confirm' · 'outcome' — one value, so the two boxes can never show together.
      mode: '',
      // A Run was clicked here and its result has not been shown (or has not come back) yet.
      ranHere: false,
      // Horizontal nudge (px) that keeps the box inside the viewport, measured on open.
      shiftX: 0,
    };
  },

  computed: {
    boxStyle() {
      return { left: `${this.shiftX}px` };
    },
  },

  watch: {
    state(next) {
      if (!next || !next.canVerify) {
        // The gate closed (flag off, permission revoked, no plan): nothing here may stay armed.
        this.ranHere = false;
        this.mode = '';
        return;
      }
      // A check started elsewhere (the card's own button, the drawer, a follow-up): this confirm is
      // no longer the thing to click.
      if (next.busy && this.mode === 'confirm') this.mode = '';
      if (!this.ranHere) return;
      if (next.outcome) {
        this.mode = 'outcome';
      } else if (!next.busy) {
        // The run (and any follow-up) ended with nothing to say: a clean verify.
        this.ranHere = false;
        if (this.mode === 'outcome') this.mode = '';
      }
    },

    mode(now) {
      if (now) {
        this.listen();
        this.$nextTick(() => {
          this.place();
          // Focus the SAFE button: Enter on a freshly opened confirm must never be a billed check.
          const el = now === 'confirm' ? this.$refs.cancel : this.$refs.closeBtn;
          if (el && typeof el.focus === 'function') el.focus({ preventScroll: true });
        });
      } else {
        this.unlisten();
      }
    },
  },

  beforeDestroy() {
    this.unlisten();
  },

  methods: {
    onButton() {
      if (!this.state || this.state.busy) return;
      if (this.mode) {
        this.close();
        return;
      }
      // A fresh ask: whatever an earlier run left behind is not this one.
      this.ranHere = false;
      this.mode = 'confirm';
    },

    /** The ONE place `run` is emitted. */
    onRun() {
      if (this.mode !== 'confirm') return;
      this.mode = '';
      if (!this.state || !this.state.canVerify || this.state.busy) return;
      this.ranHere = true;
      this.$emit('run');
    },

    /** The ONE place `benefits` is emitted. A free read: never `run`, and it takes the confirm down. */
    onBenefits() {
      if (!this.state || !this.state.canViewBenefits) return;
      if (this.mode === 'confirm') this.close();
      this.$emit('benefits');
    },

    /** Cancel, Escape, a click outside, or Close on the outcome. Never emits; forgets the run. */
    close() {
      if (this.mode === 'outcome') this.ranHere = false;
      this.mode = '';
    },

    onDocClick(e) {
      const root = this.$refs.root;
      if (root && e.target && !root.contains(e.target)) this.close();
    },

    onKey(e) {
      if (e.key === 'Escape' || e.key === 'Esc') this.close();
    },

    listen() {
      this.unlisten();
      document.addEventListener('click', this.onDocClick, true);
      document.addEventListener('keydown', this.onKey);
      window.addEventListener('resize', this.place);
    },

    unlisten() {
      document.removeEventListener('click', this.onDocClick, true);
      document.removeEventListener('keydown', this.onKey);
      window.removeEventListener('resize', this.place);
    },

    /**
     * Keep the box inside the viewport. It hangs from the button's left edge; on a phone the button
     * sits at the right of its chip, so measure the button and the box and shift the box left just
     * enough to leave EDGE px on both sides (the box is also capped at 100vw − 2·EDGE in CSS).
     */
    place() {
      const root = this.$refs.root;
      const box = this.$refs.box;
      if (!root || !box) return;
      const vw = document.documentElement.clientWidth || window.innerWidth || 0;
      if (!vw) {
        this.shiftX = 0;
        return;
      }
      const w = box.offsetWidth || Math.min(BOX_WIDTH, vw - EDGE * 2);
      const left = root.getBoundingClientRect().left;
      const wanted = Math.max(EDGE, Math.min(left, vw - w - EDGE));
      this.shiftX = Math.round(wanted - left);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../components/PatientPanel/tokens';

.iqv {
  position: relative;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  // The two buttons (Verify / Re-check, View benefits) sit side by side and may wrap in a narrow chip.
  flex-wrap: wrap;
  gap: 6px;
}

.iqv__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  margin: 0;
  padding: 0 11px;
  border: 1px solid $pp-brand-deep;
  border-radius: 8px;
  background: $pp-brand-deep;
  color: #ffffff;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) { background: $pp-brand-deeper; border-color: $pp-brand-deeper; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 2px; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
}

// The quiet sibling: same size and type as the filled button, outlined like the box's ghost buttons.
.iqv__btn--ghost {
  border-color: $pp-line;
  background: $pp-surface;
  color: $pp-brand-deep;

  &:hover:not(:disabled) { background: $pp-bg-2; border-color: $pp-line; }
}

.iqv__box {
  position: absolute;
  top: calc(100% + 8px);
  // `left` is set inline (boxStyle): the measured nudge that keeps the box on screen.
  left: 0;
  // Above the phone's sticky tab strip (z 4) and the cards under the header.
  z-index: 20;
  width: 300px;
  max-width: calc(100vw - 32px);
  box-sizing: border-box;
  padding: 14px 16px;
  border: 1px solid $pp-line;
  border-radius: $pp-radius-lg;
  background: $pp-surface;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
  color: $pp-ink;
  font-size: 13px;
  font-weight: 400;
  letter-spacing: normal;
  text-align: left;
  text-transform: none;
  white-space: normal;
}

.iqv__title {
  font-size: 14px;
  font-weight: 800;
  color: $pp-ink;
}

.iqv__facts {
  margin: 10px 0;
  font-size: 12.5px;

  div {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 3px 0;
  }

  span { flex: 0 0 auto; color: $pp-ink-3; }
  strong { min-width: 0; text-align: right; font-weight: 700; color: $pp-ink; overflow-wrap: anywhere; }
}

.iqv__cost {
  margin: 0 0 12px;
  font-size: 12.5px;
  line-height: 1.5;
  color: $pp-ink-2;
}

.iqv__line {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 10px 0 12px;
  padding: 9px 12px;
  border: 1px solid $pp-attn-line;
  border-radius: $pp-radius;
  background: $pp-attn-bg;
  font-size: 12.5px;
  line-height: 1.45;
  color: $pp-amber-ink;

  .fa { margin-top: 2px; flex: 0 0 auto; }
  span { min-width: 0; overflow-wrap: anywhere; }
}

.iqv__line--muted {
  border-color: $pp-line;
  background: $pp-bg-2;
  color: $pp-ink-3;
}

.iqv__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.iqv__act {
  height: 34px;
  margin: 0;
  padding: 0 14px;
  border-radius: $pp-radius;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;

  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 2px; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
}

.iqv__act--ghost {
  border: 1px solid $pp-line;
  background: $pp-surface;
  color: $pp-brand-deep;

  &:hover { background: $pp-bg-2; }
}

.iqv__act--primary {
  border: 1px solid $pp-brand-deep;
  background: $pp-brand-deep;
  color: #ffffff;

  &:hover:not(:disabled) { background: $pp-brand-deeper; border-color: $pp-brand-deeper; }
}
</style>
